import React from 'react';
import clsx from 'clsx';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { KeyResultCardProps } from './keyResultCard.types';
import {
  formatMetricValue,
  formatDate,
  calculateProgressStatus,
} from './keyResultCard.definitions';
import { ProgressBar } from '../ProgressBar';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared/CollaboratorAvatar';

export default function KeyResultCard({
  keyResult,
  onClick,
  onEdit,
  onDelete,
  showActions = false,
  className,
}: KeyResultCardProps) {
  const progressStatus = calculateProgressStatus(keyResult.progress);

  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger onClick if clicking on action buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onClick?.(keyResult.id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(keyResult.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(keyResult.id);
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-4',
        onClick && 'cursor-pointer',
        className
      )}
      onClick={handleClick}
    >
      {/* Header with Title and Actions */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1">
          <h4 className="text-base font-semibold text-gray-900 font-montserrat">
            {keyResult.title}
          </h4>
          {keyResult.description && (
            <p className="text-sm text-gray-600 font-montserrat mt-1">
              {keyResult.description}
            </p>
          )}
        </div>
        {showActions && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                onClick={handleEdit}
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                title="Editar Key Result"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={handleDelete}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Excluir Key Result"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2 text-sm font-montserrat">
          <span className="text-gray-600">Inicial:</span>
          <span className="font-semibold text-gray-900">
            {formatMetricValue(
              keyResult.initialValue,
              keyResult.metricType,
              keyResult.unit
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm font-montserrat">
          <span className="text-gray-600">→</span>
        </div>
        <div className="flex items-center gap-2 text-sm font-montserrat">
          <span className="text-gray-600">Meta:</span>
          <span className="font-semibold text-blue-600">
            {formatMetricValue(
              keyResult.targetValue,
              keyResult.metricType,
              keyResult.unit
            )}
          </span>
        </div>
      </div>

      {/* Current Value */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 font-montserrat">
            Valor Atual
          </span>
          <span className="text-lg font-bold text-gray-900 font-montserrat">
            {formatMetricValue(
              keyResult.currentValue,
              keyResult.metricType,
              keyResult.unit
            )}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <ProgressBar
          progress={keyResult.progress}
          status={progressStatus}
          size="md"
          showPercentage={true}
          animated={true}
        />
      </div>

      {/* Footer with Last Check-In and Responsible */}
      <div className="flex items-center justify-between gap-3">
        {/* Last Check-In */}
        {keyResult.lastCheckInDate && (
          <div className="flex items-center gap-2 text-xs text-gray-500 font-montserrat">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              Último check-in: {formatDate(keyResult.lastCheckInDate)}
            </span>
          </div>
        )}
        {/* Responsible Avatar */}
        {keyResult.responsibleName && (
          <div className="flex items-center gap-2">
            <CollaboratorAvatar
              photoUrl={keyResult.responsiblePhotoUrl}
              name={keyResult.responsibleName}
              size="xsmall"
            />
            <span className="text-xs text-gray-600 font-montserrat">
              {keyResult.responsibleName}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
